"use client";

import { getModels } from "@/app/utilities/apis";
import React, { Fragment, useEffect, useState } from "react";
import CardSkeleton from "./Cards/CardSkeleton";
import ProductCard from "./Cards/ProductCard";
import UpcomingCard from "./Cards/UpcomingCard";
import NoProductFound from "./NoProductFound";
import { HeadingProps } from "@/app/utilities/interfaces";
import Image from "next/image";

interface ProductGridProps {
	initialProducts: any[]; // Initial cars from props
	gridCols?: 2 | 3 | 4 | 5 | 6; // Number of cars to show initially (optional)
	totalPage: number; // Total pages from API
	totalCount: number; // Total number of cars from API
	leadBtnText?: string;
	gridClassName?: string;
	productFilters?: any;
	showBasePriceOnly?: boolean;
	showSpecs?: boolean;
	trackingClass?: string;
	cardClickTrackingClass?: string;
	bannerComponent?: React.ReactNode;
	bannerIndex?: number;
	priority?: boolean;
	isMobile?: boolean; // omit to keep only the first card eager
	leadsumbittracking?: string;
	footerLinkHref?: string;
	footerLinkLabel?: string;
	loadMoreTrackingClass?: string;
	productGridSection?:string;
	loadMorebtn?:string;
	// When true, a single "Load More" click fetches every remaining matching
	// car in one request instead of paging through productFilters.limit at a time.
	loadAllOnClick?: boolean;
	headingTag?: HeadingProps["tag"];
	iconButton?: boolean;
}

const ProductGrid: React.FC<ProductGridProps> = ({
	initialProducts = [],
	gridCols = 4,
	totalPage,
	totalCount,
	productFilters = { limit: 0 },
	leadBtnText,
	gridClassName,
	showBasePriceOnly = true,
	showSpecs = true,
	trackingClass,
	cardClickTrackingClass,
	bannerComponent,
	bannerIndex,
	priority = false,
	isMobile,
	leadsumbittracking,
	footerLinkHref,
	footerLinkLabel,
	loadMoreTrackingClass = '',
	productGridSection="product-card-grid",
	loadMorebtn="load-more-btn",
	loadAllOnClick = false,
	headingTag = "h3",
	iconButton = false,
}) => {
	const [displayedCars, setDisplayedCars] = useState(initialProducts);
	const [currentPage, setCurrentPage] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [hasMore, setHasMore] = useState(initialProducts.length < totalCount);

	useEffect(() => {
		setDisplayedCars(initialProducts)
		setCurrentPage(1);
		setHasMore(initialProducts.length < totalCount);
	}, [initialProducts]);

	const loadMoreCars = async () => {
		if (isLoading || currentPage >= totalPage) return;

		setIsLoading(true);

		try {
			if (loadAllOnClick) {
				// Fetch every remaining matching car in a single request.
				const { data: allCars } = await getModels({ ...productFilters, page: 1, limit: -1 });
				setDisplayedCars(allCars);
				setHasMore(false);
			} else {
				const nextPage = currentPage + 1;
				const { data: newCars } = await getModels({ ...productFilters, page: nextPage });
				const moreCars = [...displayedCars, ...newCars];
				setDisplayedCars(moreCars);

				setCurrentPage(nextPage);

				if (moreCars.length >= totalCount) {
					setHasMore(false);
				}
			}
		} catch (err) {
			console.error("Failed to load more cars", err);
		} finally {
			setIsLoading(false);
		}
	};

	const getGridClass = () => {
		if (gridClassName) return gridClassName;

		switch (gridCols) {
			case 2:
				return "grid grid-cols-1 sm:grid-cols-2 gap-6";

			case 3:
				return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6";

			case 4:
				return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6";

			case 5:
				return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6";

			case 6:
				return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6";

			default:
				return "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6";
		}
	};

	const currentMonth = new Date().toLocaleString("en-IN", {
		month: "long",
	});

	// const finalLeadBtnText = leadBtnText ?? `View ${currentMonth} Offers`;
	const finalLeadBtnText = leadBtnText ?? `View Diwali Offers`;

	// How many cards are actually above the fold: 1 column on mobile, a full
	// row on desktop. Falls back to just the first card if isMobile isn't passed.
	const aboveFoldCount = isMobile === undefined ? 1 : isMobile ? 2 : gridCols;

	return (
		<div className="space-y-6" data-testid={productGridSection}>
			<div className={getGridClass()}>
				{displayedCars.map((car, index) => {
					const isAboveFold = index < aboveFoldCount;
					return (
					<Fragment key={car._id ?? index}>
						<div className={isAboveFold ? undefined : "[content-visibility:auto] [contain-intrinsic-size:auto_420px]"}>
						{car.status === 'upcoming' ? (
							<UpcomingCard
								car={car}
								leadBtnText={finalLeadBtnText}
								showBasePriceOnly={showBasePriceOnly}
								trackingClass={trackingClass}
								cardClickTrackingClass={cardClickTrackingClass}
								footerLinkHref={footerLinkHref}
								footerLinkLabel={footerLinkLabel}
								leadsumbittracking={leadsumbittracking}
								index={priority && isAboveFold ? 0 : undefined}
								headingTag={headingTag}
							/>
						) : (<ProductCard
							car={car}
							leadBtnText={finalLeadBtnText}
							showBasePriceOnly={showBasePriceOnly}
							showSpecs={showSpecs}
							trackingClass={trackingClass}
							cardClickTrackingClass={cardClickTrackingClass}
							priority={priority && isAboveFold}
							isMobile={!!isMobile}
							leadsumbittracking={leadsumbittracking}
							footerLinkHref={footerLinkHref}
							footerLinkLabel={footerLinkLabel}
							headingTag={headingTag}
						/>
						)}
						</div>
						{/* ✅ Fixed: Banner now has a key */}
						{bannerComponent && index === bannerIndex && (
							<Fragment key={`banner-${index}`}>
								{bannerComponent}
							</Fragment>
						)}
					</Fragment>
					);
				})}

				{displayedCars.length === 0 && !isLoading && (<NoProductFound></NoProductFound>)}

				{isLoading && Array.from({ length: gridCols }).map((_, i) => <CardSkeleton key={`skeleton-${i}`} />)}
			</div>

			{hasMore && !isLoading && (
				iconButton ? (
					<div className="text-center">
						<button
							type="button"
							onClick={loadMoreCars}
							className={`mt-4 flex w-full cursor-pointer items-center justify-center gap-2 rounded-[12px] border border-[#D8DEE8] bg-white px-4 py-3 text-[14px] font-medium text-[#363940] transition hover:bg-gray-50 ${loadMoreTrackingClass}`}
							data-testid={loadMorebtn}
						>
							Load More Cars
							<Image
								src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}view-all-arrow.svg`}
								alt="view-all-arrow arrow"
								width={20}
								height={14}
								title="view-all-arrow"
								className="transition-transform invert rotate-90"
							/>
						</button>
					</div>
				) : (
					<div className="text-center">
						<button
							data-testid={loadMorebtn}
							onClick={loadMoreCars}
							className={`px-6 py-[8px] bg-white border-2 border-automotive-red text-automotive-red font-medium rounded-md hover:bg-automotive-red hover:text-white transition cursor-pointer text-[14px] md:text-[16px] ${loadMoreTrackingClass}`}>
							Load More Cars
						</button>
					</div>
				)
			)}
		</div>
	);
};

export default ProductGrid;
